import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconBoxMultiple,
  IconDeviceFloppy,
  IconFolderOpen,
  IconTrash,
  IconWand,
} from "@tabler/icons-react";
import { RibbonButton, RibbonSection, useFilePicker } from "@tredespace/ui/widgets";
import type { JSX } from "react";
import { clearScaffold, redo, undo } from "../../state/engine/engine.actions.ts";
import {
  exportScaffoldGlb,
  openDesign,
  runGenerator,
  saveDesign,
} from "../../state/generator/generator.actions.ts";
import { historyState } from "../../state/history/history.state.ts";
import { setHud } from "../../state/hud/hud.actions.ts";

/**
 * Medium buttons stack two to a column, so the order below reads down each
 * column in turn: Undo/Redo, then Load/Save, then Export/Clear.
 */
export function ModelSection(): JSX.Element {
  const { canUndo, canRedo } = historyState.use();
  const picker = useFilePicker(".json,application/json", (file) => {
    void openDesign(file).then((result) => {
      if (result.error) {
        setHud(result.error.msg);
      }
    });
  });

  return (
    <RibbonSection title="Model">
      {picker.element}
      <RibbonButton
        icon={<IconWand />}
        label="Generate"
        size="big"
        onClick={runGenerator}
        tooltip={"Build a regular run from the Generator panel\nReplaces the current model"}
      />

      <RibbonButton
        icon={<IconArrowBackUp />}
        label="Undo"
        size="medium"
        disabled={!canUndo}
        onClick={undo}
        tooltip="Undo (Ctrl+Z)"
      />
      <RibbonButton
        icon={<IconArrowForwardUp />}
        label="Redo"
        size="medium"
        disabled={!canRedo}
        onClick={redo}
        tooltip="Redo (Ctrl+Y or Ctrl+⇧+Z)"
      />

      <RibbonButton
        icon={<IconFolderOpen />}
        label="Load"
        size="medium"
        onClick={picker.open}
        tooltip="Open a design JSON"
      />
      <RibbonButton
        icon={<IconDeviceFloppy />}
        label="Save"
        size="medium"
        onClick={saveDesign}
        tooltip="Save the design as JSON"
      />

      <RibbonButton
        icon={<IconBoxMultiple />}
        label="Export model"
        size="medium"
        onClick={() => void exportScaffoldGlb()}
        tooltip={
          "Export the scaffold alone as GLB\n" +
          "At world coordinates, so it drops back into the source model\n" +
          "The reference model and ground are not included"
        }
      />
      <RibbonButton
        icon={<IconTrash />}
        label="Clear"
        size="medium"
        onClick={clearScaffold}
        tooltip="Remove everything"
      />
    </RibbonSection>
  );
}
